import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import { OrderInboundAssignedEmployee } from './types';
import {
  OrderInboundsAssignedEmployee,
  UnassignedEmployeeTrigger,
} from './order-inbounds-assigned-employee';
import { OrderInboundsStackParams } from '@/navigators/types';
import {
  OrderInboundWrapper,
  Props as OrderInboundWrapperProps,
} from './order-inbound-wrapper';
import { PageContainer } from '@/components/page-container';
import { Platform, StatusBar } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { useLayoutContext } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateAssignedEmployee } from './queries';
import { WEB_MARGIN_TOP } from './utils';

type Props = StackScreenProps<
  OrderInboundsStackParams,
  ORDER_INBOUNDS_ROUTES.Detail
>;

const BACK_BUTTON_TEST_ID = 'order-inbounds-detail-back-icon';

export const OrderInboundsDetailScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const {
    initialAssignedEmployee,
    initialRestaurantName,
    initialRestaurantUUID,
    initialSupplierUUID,
    initialProcessing,
    initialMergeUUID,
    initialRejectedUpdatedAt,
    initialSubmittedOrderUUID,
    initialThreadIds,
    onClose,
    orderUUID,
    unknownContactInfo,
    setOrderInboundsListDrawerOpen,
  } = route.params;
  const [styles, theme] = useStyles();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();

  // Determine if we should use white background
  const shouldUseWhiteBackground = isSmallLayout || isSmallTabletLayout;

  const [assignedEmployee, setAssignedEmployee] = useState<
    OrderInboundAssignedEmployee | undefined
  >(initialAssignedEmployee);
  const [orderIdentity, setOrderIdentity] = useState<{
    restaurantName: string | null;
    supplierUUID: string;
  }>({
    restaurantName: initialRestaurantName,
    supplierUUID: initialSupplierUUID,
  });

  const inboundsDetailProps: OrderInboundWrapperProps = {
    initialMergeUUID,
    initialProcessing,
    initialRejectedUpdatedAt,
    initialRestaurantUUID,
    initialSubmittedOrderUUID,
    initialThreadIds,
    isInboundsListExpanded: false,
    onAssignedEmployeeChanged: (assignedEmployee) =>
      setAssignedEmployee(assignedEmployee ?? undefined),
    onClose,
    onOrderIdentityChanged: (identity) =>
      setOrderIdentity((prev) =>
        prev.restaurantName === identity.restaurantName &&
        prev.supplierUUID === identity.supplierUUID
          ? prev
          : identity,
      ),
    orderUUID,
    selectNextSmartOrder: () => {},
    setIsInboundsListExpanded: () => {},
    setOrderInboundsListDrawerOpen,
    unknownContactInfo,
  };

  const webPaddingStyle = Platform.OS === 'web' ? styles.pageHeaderWeb : null;

  const updateAssignedEmployee = useUpdateAssignedEmployee();

  const onUpdateAssignedEmployeeActionClick = useCallback(
    async (employeeUUID: string | null) => {
      try {
        await updateAssignedEmployee({
          employeeUUID,
          smartOrderUUIDs: [orderUUID],
          supplierUUID: orderIdentity.supplierUUID,
        });
      } catch (err) {
        showErrorToast();
      }
    },
    [
      orderIdentity.supplierUUID,
      orderUUID,
      showErrorToast,
      updateAssignedEmployee,
    ],
  );

  useEffect(() => {
    if (!isSmallLayout && !isSmallTabletLayout) {
      navigation.navigate(ORDER_INBOUNDS_ROUTES.List, {
        initialSmartOrderUUID: orderUUID,
      });
    }
  }, [isSmallLayout, isSmallTabletLayout, navigation, orderUUID]);

  const actions: NavigationHeaderProps['actions'] = useMemo(() => {
    const navActions: NavigationHeaderProps['actions'] = [
      {
        component: (
          <OrderInboundsAssignedEmployee
            assignedEmployee={assignedEmployee}
            onAssignEmployee={onUpdateAssignedEmployeeActionClick}
            onUnassignEmployee={async () => {
              await onUpdateAssignedEmployeeActionClick(null);
            }}
            renderUnassignedTrigger={(onPress) => (
              <UnassignedEmployeeTrigger onPress={onPress} />
            )}
            restaurantName={orderIdentity.restaurantName}
            smartOrderUUID={orderUUID}
            supplierUUID={orderIdentity.supplierUUID}
          />
        ),
      },
    ];

    return navActions;
  }, [
    assignedEmployee,
    onUpdateAssignedEmployeeActionClick,
    orderIdentity,
    orderUUID,
  ]);

  return (
    <>
      <StatusBar barStyle="dark-content" />
      {isSmallLayout ? (
        <PageContainer
          animateNavigationHeader
          containerStyle={
            shouldUseWhiteBackground
              ? styles.backgroundWhite
              : styles.background
          }
          navigationHeaderProps={{
            actions,
            backButtonTestID: BACK_BUTTON_TEST_ID,
            containerStyle: shouldUseWhiteBackground
              ? styles.backgroundWhite
              : styles.background,
            contentContainerStyle: webPaddingStyle,
            onBackPress: () => onClose?.(),
            showBackButton: Boolean(onClose),
            showShadow: false,
          }}
          showNavigationHeader
          statusBarColor={
            shouldUseWhiteBackground
              ? theme.colors.white
              : theme.sk.color.bg.secondary.default
          }
        >
          {(scrollViewProps) => (
            <OrderInboundWrapper
              key={orderUUID}
              {...inboundsDetailProps}
              scrollViewProps={scrollViewProps}
            />
          )}
        </PageContainer>
      ) : (
        <PageContainer
          containerStyle={
            shouldUseWhiteBackground
              ? styles.backgroundWhite
              : styles.background
          }
          navigationHeaderProps={{
            containerStyle: styles.pageHeaderTabletContainer,
            hideHeader: true,
          }}
          statusBarColor={
            shouldUseWhiteBackground ? theme.colors.white : theme.colors.white
          }
        >
          <OrderInboundWrapper key={orderUUID} {...inboundsDetailProps} />
        </PageContainer>
      )}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  background: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  backgroundWhite: {
    backgroundColor: theme.colors.white,
  },
  pageHeaderTabletContainer: {
    paddingTop: 0,
  },
  pageHeaderWeb: {
    paddingTop: WEB_MARGIN_TOP,
  },
}));
